import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Search, Filter, Plus, Edit2, DollarSign, CheckCircle,
  Users, Calendar, MapPin, Phone, ArrowLeft, Save, X, CreditCard,
  ChevronDown, ChevronUp, FileText
} from 'lucide-react';

// ─── Datos de ejemplo en formato JSON del formulario ─────────────────────────

const INITIAL_QUOTES = [
  {
    meta: { submittedAt: '2026-04-28T22:44:10.815Z', formVersion: '2.0', tipo: 'cotizacion_completa' },
    seccion1_destino: { id: 'wdw', nombre: 'Walt Disney World', ubicacion: 'Orlando, Florida', region: 'usa' },
    seccion2_documentacion: { pasaporte: 'yes', visa: 'yes', avisoVisa: null },
    seccion3_datos_personales: { nombre: 'Samuel Burciaga', email: 'samuel@gmail.com', whatsapp: '+543334801569', ciudad: 'Denver', ciudadSalida: 'Denver' },
    seccion4_experiencia: { tipo: 'full_pkg', viajeros: '4', edades: '2 adultos, 2 niños de 8 y 11 años', tipoViaje: 'Familia' },
    seccion5_presupuesto: { presupuesto: 'Menos de $1,000 USD', nivelAsesoria: 'full' },
    seccion6_boletos: null,
    seccion7_expectativas: { viajoAntes: 'yes', expectativas: 'Quiero que mis hijos vean a Mickey por primera vez.', nivelDecision: 3, pagoEstimado: 'Este mes', comoNosConocio: 'Instagram' },
    _admin: { id: 'COT-2026-001', total: null, isPaid: false, notas: '' },
  },
  {
    meta: { submittedAt: '2026-04-27T15:10:00.000Z', formVersion: '2.0', tipo: 'cotizacion_completa' },
    seccion1_destino: { id: 'dlparis', nombre: 'Disneyland París', ubicacion: 'Chessy, Francia · Europa', region: 'eu' },
    seccion2_documentacion: { pasaporte: 'yes', visa: null, avisoVisa: null },
    seccion3_datos_personales: { nombre: 'Laura Pérez', email: 'laura@gmail.com', whatsapp: '+521234567890', ciudad: 'CDMX', ciudadSalida: 'Ciudad de México' },
    seccion4_experiencia: { tipo: 'full_pkg', viajeros: '2', edades: '2 adultos', tipoViaje: 'Pareja' },
    seccion5_presupuesto: { presupuesto: '$2,500 – $4,000 USD', nivelAsesoria: 'basic' },
    seccion6_boletos: null,
    seccion7_expectativas: { viajoAntes: 'no', expectativas: 'Luna de miel soñada en París.', nivelDecision: 5, pagoEstimado: 'En 2 semanas', comoNosConocio: 'Recomendación' },
    _admin: { id: 'COT-2026-002', total: 3200, isPaid: true, notas: 'Pago recibido vía transferencia.' },
  },
  {
    meta: { submittedAt: '2026-04-26T09:00:00.000Z', formVersion: '2.0', tipo: 'cotizacion_completa' },
    seccion1_destino: { id: 'uso', nombre: 'Universal Studios', ubicacion: 'Orlando, Florida', region: 'usa' },
    seccion2_documentacion: { pasaporte: 'partial', visa: 'partial', avisoVisa: null },
    seccion3_datos_personales: { nombre: 'Carlos Mendoza', email: 'carlos@gmail.com', whatsapp: '+527771234567', ciudad: 'Guadalajara', ciudadSalida: 'Guadalajara' },
    seccion4_experiencia: { tipo: 'tickets', viajeros: '3', edades: '3 adultos', tipoViaje: 'Amigos' },
    seccion5_presupuesto: { presupuesto: '$1,000 – $2,500 USD', nivelAsesoria: 'tickets' },
    seccion6_boletos: { tiposBoletos: ['Universal Orlando'], fechas: 'Del 10 al 15 de julio', asesoriamagica: 'yes' },
    seccion7_expectativas: { viajoAntes: 'yes', expectativas: 'Queremos vivir Harry Potter World.', nivelDecision: 4, pagoEstimado: 'En 3 semanas', comoNosConocio: 'Google' },
    _admin: { id: 'COT-2026-003', total: 1800, isPaid: false, notas: '' },
  },
  {
    meta: { submittedAt: '2026-04-25T11:30:00.000Z', formVersion: '2.0', tipo: 'lead_sin_visa' },
    seccion1_destino: { id: 'wdw', nombre: 'Walt Disney World', ubicacion: 'Orlando, Florida', region: 'usa' },
    seccion2_documentacion: { pasaporte: 'yes', visa: 'no', avisoVisa: 'yes' },
    lead_sin_visa: { nombre: 'María Torres', email: 'maria@gmail.com', recordatorio: 'yes' },
    _admin: { id: 'COT-2026-004', total: null, isPaid: false, notas: 'Sin visa. Enviar guía.' },
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PASSPORT_LABEL = { yes: '✅ Todos', partial: '⚠️ Algunos', no: '❌ Ninguno' };
const VISA_LABEL     = { yes: '✅ Todos', partial: '⚠️ Algunos', no: '❌ Sin visa' };
const ADVISORY_LABEL = { full: 'Completa', basic: 'Básica', tickets: 'Solo boletos' };
const EXPERIENCE_LABEL = { full_pkg: 'Paquete completo', tickets: 'Solo entradas', cruise: 'Crucero Disney', other: 'Otro' };

const destIcon = { wdw: '🏰', dlca: '🏰', uso: '🎬', ush: '🎬', cruise: '🚢', dlparis: '🏰' };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getName = (q) =>
  q.seccion3_datos_personales?.nombre || q.lead_sin_visa?.nombre || '—';

const getStatus = (q) => {
  if (q._admin.isPaid)          return { label: 'PAGADO',     color: 'green'  };
  if (!q._admin.total)          return { label: 'SIN PRECIO', color: 'orange' };
  return                               { label: 'PENDIENTE',  color: 'yellow' };
};

const statusClasses = {
  green:  { dot: 'bg-green-500',  badge: 'bg-green-100 text-green-800'   },
  orange: { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800' },
  yellow: { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800' },
};

// ─── Componente: Detalle expandido de cotización ──────────────────────────────

const QuoteDetail = ({ q }) => {
  const d   = q.seccion1_destino;
  const doc = q.seccion2_documentacion;
  const p   = q.seccion3_datos_personales;
  const exp = q.seccion4_experiencia;
  const pre = q.seccion5_presupuesto;
  const bol = q.seccion6_boletos;
  const ex7 = q.seccion7_expectativas;
  const lead = q.lead_sin_visa;
  const isLead = q.meta.tipo === 'lead_sin_visa';

  const Row = ({ label, value }) =>
    value ? (
      <div className="flex justify-between items-start gap-4 py-1.5 border-b border-gray-100 last:border-0">
        <span className="text-xs text-gray-400 font-medium shrink-0 w-36">{label}</span>
        <span className="text-sm text-gray-700 font-semibold text-right">{value}</span>
      </div>
    ) : null;

  const Card = ({ emoji, title, children }) => (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm">{emoji}</span>
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-4">

      {/* Documentación */}
      <Card emoji="🛂" title="Documentación">
        <Row label="Pasaporte" value={PASSPORT_LABEL[doc?.pasaporte]} />
        {doc?.visa && <Row label="Visa EE.UU." value={VISA_LABEL[doc.visa]} />}
        {doc?.avisoVisa && <Row label="Recordatorio visa" value={doc.avisoVisa === 'yes' ? '✅ Sí' : '❌ No'} />}
      </Card>

      {/* Contacto */}
      {(p || lead) && (
        <Card emoji="👤" title="Contacto">
          <Row label="Nombre"        value={p?.nombre        || lead?.nombre} />
          <Row label="Email"         value={p?.email         || lead?.email}  />
          <Row label="WhatsApp"      value={p?.whatsapp}     />
          <Row label="Residencia"    value={p?.ciudad}       />
          <Row label="Ciudad salida" value={p?.ciudadSalida} />
        </Card>
      )}

      {/* Experiencia */}
      {exp && (
        <Card emoji="🎢" title="Experiencia">
          <Row label="Tipo"          value={EXPERIENCE_LABEL[exp.tipo]} />
          <Row label="Viajeros"      value={exp.viajeros ? `${exp.viajeros} persona(s)` : null} />
          <Row label="Edades"        value={exp.edades}    />
          <Row label="Tipo de viaje" value={exp.tipoViaje} />
        </Card>
      )}

      {/* Presupuesto */}
      {pre && (
        <Card emoji="💰" title="Presupuesto">
          <Row label="Por persona"   value={pre.presupuesto}                    />
          <Row label="Asesoría"      value={ADVISORY_LABEL[pre.nivelAsesoria]}  />
        </Card>
      )}

      {/* Boletos */}
      {bol && (
        <Card emoji="🎟️" title="Boletos">
          <Row label="Tipos"           value={bol.tiposBoletos?.join(', ')}                      />
          <Row label="Fechas"          value={bol.fechas}                                         />
          <Row label="Asesoría mágica" value={bol.asesoriamagica === 'yes' ? '✅ Sí' : '❌ No'} />
        </Card>
      )}

      {/* Expectativas */}
      {ex7 && (
        <Card emoji="🌟" title="Expectativas">
          <Row label="¿Viajó antes?"   value={ex7.viajoAntes === 'yes' ? '✅ Sí' : '🌟 Primera vez'} />
          <Row label="Decisión"        value={ex7.nivelDecision ? `${ex7.nivelDecision} / 5` : null} />
          <Row label="Primer pago"     value={ex7.pagoEstimado}    />
          <Row label="Nos conoció por" value={ex7.comoNosConocio}  />
          {ex7.expectativas && (
            <div className="pt-2 mt-1 border-t border-gray-100">
              <p className="text-xs text-gray-400 font-medium mb-1">Lo que espera vivir</p>
              <p className="text-sm text-gray-700 leading-relaxed italic">"{ex7.expectativas}"</p>
            </div>
          )}
        </Card>
      )}

      {/* Notas admin */}
      {q._admin.notas && (
        <Card emoji="📝" title="Notas internas">
          <p className="text-sm text-gray-700">{q._admin.notas}</p>
        </Card>
      )}
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const AdminDashboard = () => {
  const [quotes, setQuotes]           = useState(INITIAL_QUOTES);
  const [currentView, setCurrentView] = useState('list');
  const [searchTerm, setSearchTerm]   = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId]   = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);

  const [showPriceModal, setShowPriceModal]     = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedIdx, setSelectedIdx]           = useState(null);
  const [priceInput, setPriceInput]             = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  // ── Filtros ────────────────────────────────────────────────────────────────
  const filtered = quotes.filter((q) => {
    const name  = getName(q).toLowerCase();
    const dest  = q.seccion1_destino?.nombre?.toLowerCase() || '';
    const id    = q._admin.id.toLowerCase();
    const phone = q.seccion3_datos_personales?.whatsapp || q.lead_sin_visa?.email || '';
    const term  = searchTerm.toLowerCase();

    const matchSearch = !searchTerm ||
      name.includes(term) || dest.includes(term) ||
      id.includes(term)   || phone.includes(term);

    const { label } = getStatus(q);
    const matchFilter =
      filterStatus === 'all'      ? true :
      filterStatus === 'paid'     ? label === 'PAGADO'     :
      filterStatus === 'unpaid'   ? label === 'PENDIENTE'  :
      filterStatus === 'no-price' ? label === 'SIN PRECIO' : true;

    return matchSearch && matchFilter;
  });

  // ── Acciones ───────────────────────────────────────────────────────────────
  const updateAdmin = (idx, patch) => {
    setQuotes(prev => prev.map((q, i) =>
      i === idx ? { ...q, _admin: { ...q._admin, ...patch } } : q
    ));
  };

  const handleAssignPrice = (idx) => { setSelectedIdx(idx); setPriceInput(''); setShowPriceModal(true); };
  const confirmPrice = () => {
    const val = parseFloat(priceInput);
    if (!val || isNaN(val)) return;
    updateAdmin(selectedIdx, { total: val });
    setShowPriceModal(false);
  };

  const handleOpenPayment = (idx) => { setSelectedIdx(idx); setShowPaymentModal(true); };
  const handleProcessPayment = () => {
    setProcessingPayment(true);
    setTimeout(() => {
      updateAdmin(selectedIdx, { isPaid: true });
      setShowPaymentModal(false);
      setProcessingPayment(false);
    }, 2000);
  };

  const handleSaveNotes = (idx, notas) => updateAdmin(idx, { notas });

  // ── Importar JSON ──────────────────────────────────────────────────────────
  const handleImportJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const incoming = Array.isArray(parsed) ? parsed : [parsed];
        const withAdmin = incoming.map((q, i) => ({
          ...q,
          _admin: q._admin || {
            id: `COT-IMP-${String(quotes.length + i + 1).padStart(3, '0')}`,
            total: null, isPaid: false, notas: '',
          },
        }));
        setQuotes(prev => [...prev, ...withAdmin]);
      } catch { alert('El archivo no es un JSON válido.'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex justify-between items-center mb-10">
            <Link
              to="/"
              className="flex items-center text-gray-700 hover:text-pink-500 transition-colors bg-white px-4 py-2 rounded-lg shadow-md"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Inicio
            </Link>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Dashboard Admin 🐭
            </h1>
            <div className="w-32" />
          </div>

          {/* Controles */}
          <div className="bg-white rounded-xl shadow-lg p-5 mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Búsqueda */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, destino, ID..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Filtro */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Todas</option>
                  <option value="paid">Pagadas</option>
                  <option value="unpaid">Pendientes</option>
                  <option value="no-price">Sin precio</option>
                </select>
              </div>

              {/* Importar JSON */}
              {/* <label className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-700 px-4 py-2 rounded-lg font-medium text-sm cursor-pointer hover:bg-indigo-100 transition-colors">
                <FileText className="w-4 h-4" />
                Importar JSON
                <input type="file" accept=".json" className="hidden" onChange={handleImportJSON} />
              </label> */}
            </div>

            {/* Stats rápidas */}
            <div className="grid grid-cols-3 gap-4 mt-5 pt-4 border-t border-gray-100">
              {[
                { label: 'Total',      value: quotes.length,                                           color: 'text-gray-700'   },
                { label: 'Pagadas',    value: quotes.filter(q => q._admin.isPaid).length,              color: 'text-green-600'  },
                { label: 'Pendientes', value: quotes.filter(q => !q._admin.isPaid && q._admin.total).length, color: 'text-yellow-600' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lista */}
          <div className="flex flex-col gap-4">
            {filtered.length === 0 && (
              <div className="text-center py-16 text-gray-400">No se encontraron cotizaciones.</div>
            )}

            {filtered.map((q, i) => {
              const realIdx   = quotes.indexOf(q);
              const status    = getStatus(q);
              const sc        = statusClasses[status.color];
              const name      = getName(q);
              const dest      = q.seccion1_destino;
              const isExpanded = expandedId === q._admin.id;
              const isLead    = q.meta.tipo === 'lead_sin_visa';

              return (
                <motion.div
                  key={q._admin.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow overflow-hidden"
                >
                  {/* Fila principal */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      {/* Izquierda: estado + ID + destino */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${sc.dot}`} />
                        <span className="font-bold text-gray-800 text-sm">{q._admin.id}</span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.badge}`}>
                          {status.label}
                        </span>
                        {isLead && (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                            LEAD SIN VISA
                          </span>
                        )}
                      </div>

                      {/* Derecha: acciones */}
                      <div className="flex items-center gap-1 shrink-0">
                        {!q._admin.isPaid && !isLead && (
                          <button onClick={() => { setEditingQuote({ idx: realIdx, notas: q._admin.notas }); }}
                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Agregar nota">
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        {q._admin.total === null && (
                          <button onClick={() => handleAssignPrice(realIdx)}
                            className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors" title="Asignar precio">
                            <DollarSign className="w-4 h-4" />
                          </button>
                        )}
                        {q._admin.total !== null && !q._admin.isPaid && (
                          <button onClick={() => handleOpenPayment(realIdx)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Marcar como pagado">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : q._admin.id)}
                          className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors ml-1"
                          title="Ver detalle"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Info resumen */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{destIcon[dest?.id] || '🗺️'}</span>
                        <div>
                          <p className="text-xs text-gray-400">Destino</p>
                          <p className="text-sm font-semibold text-gray-700">{dest?.nombre || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Cliente</p>
                          <p className="text-sm font-semibold text-gray-700">{name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Ubicación</p>
                          <p className="text-sm font-semibold text-gray-700">{dest?.ubicacion || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Enviado</p>
                          <p className="text-sm font-semibold text-gray-700">{formatDate(q.meta.submittedAt)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="mt-3 flex justify-between items-center">
                      <p className="text-xs text-gray-400">
                        {q.seccion4_experiencia?.viajeros
                          ? `${q.seccion4_experiencia.viajeros} viajero(s) · ${q.seccion4_experiencia.tipoViaje || ''}`
                          : isLead ? 'Lead sin visa' : '—'}
                      </p>
                      {q._admin.total !== null ? (
                        <p className="text-xl font-bold text-green-600">${q._admin.total.toLocaleString()}</p>
                      ) : (
                        <p className="text-sm font-medium text-orange-500">Sin precio asignado</p>
                      )}
                    </div>
                  </div>

                  {/* Detalle expandido */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden border-t border-gray-100"
                      >
                        <div className="px-5 pb-5">
                          <QuoteDetail q={q} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal: Notas */}
      {editingQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Notas internas</h3>
              <button onClick={() => setEditingQuote(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              rows={4}
              value={editingQuote.notas}
              onChange={e => setEditingQuote({ ...editingQuote, notas: e.target.value })}
              placeholder="Escribe notas internas sobre esta cotización..."
              className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditingQuote(null)}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button onClick={() => { handleSaveNotes(editingQuote.idx, editingQuote.notas); setEditingQuote(null); }}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 hover:shadow-lg transition-all">
                <Save className="w-4 h-4" /> Guardar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal: Asignar precio */}
      {showPriceModal && selectedIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Asignar precio</h3>
            <p className="text-sm text-gray-500 mb-5">
              {quotes[selectedIdx]._admin.id} · {quotes[selectedIdx].seccion1_destino?.nombre}
            </p>
            <div className="relative mb-6">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
              <input type="number" value={priceInput} onChange={e => setPriceInput(e.target.value)}
                placeholder="0.00" step="0.01" autoFocus
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 text-lg" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowPriceModal(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-xl font-medium hover:bg-gray-200">Cancelar</button>
              <button onClick={confirmPrice}
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2.5 rounded-xl font-medium hover:shadow-lg transition-all">
                Asignar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal: Procesar pago */}
      {showPaymentModal && selectedIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-gray-800">Confirmar pago</h3>
              <button onClick={() => setShowPaymentModal(false)} disabled={processingPayment}
                className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-gray-500">Cotización</span><span className="font-semibold">{quotes[selectedIdx]._admin.id}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cliente</span><span className="font-semibold">{getName(quotes[selectedIdx])}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Destino</span><span className="font-semibold">{quotes[selectedIdx].seccion1_destino?.nombre}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Viajeros</span><span className="font-semibold">{quotes[selectedIdx].seccion4_experiencia?.viajeros || '—'}</span></div>
              <hr />
              <div className="flex justify-between text-base"><span className="font-bold text-gray-800">Total</span>
                <span className="font-bold text-green-600">${quotes[selectedIdx]._admin.total?.toLocaleString()}</span></div>
            </div>

            {processingPayment && (
              <div className="bg-yellow-50 rounded-xl p-4 mb-4 text-center">
                <div className="flex justify-center gap-1 mb-2">
                  {[0, 0.1, 0.2].map((d, i) => (
                    <div key={i} className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: `${d}s` }} />
                  ))}
                </div>
                <p className="text-yellow-700 text-sm font-medium">Procesando pago...</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowPaymentModal(false)} disabled={processingPayment}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-xl font-medium disabled:opacity-50">Cancelar</button>
              <button onClick={handleProcessPayment} disabled={processingPayment}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-lg transition-all">
                {processingPayment
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Procesando...</>
                  : <><CheckCircle className="w-4 h-4" />Confirmar pago</>}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;